import { useEffect, useState, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Tooltip, List, ListItem, ListItemButton, ListItemText, Paper, ClickAwayListener, Divider, Popper } from '@mui/material';

import useZustandStore from 'zustandStore';

import mainStyle from 'components/Select/mainStyle.module.scss';

import { ReactComponent as DropdownIcon } from 'assets/images/icons/icn_pagination_chevron_down.svg';
import { ReactComponent as ChevronUp } from 'assets/images/icons/icn_pagination_chevron_up.svg';
import { ReactComponent as ChevronUpHover } from 'assets/images/icons/icn_pagination_chevron_up_hover.svg';
import { ReactComponent as ChevronDownHover } from 'assets/images/icons/icn_pagination_chevron_down_hover.svg';
import { ReactComponent as AddIcon } from 'assets/images/icons/btn_add.svg';
import { ReactComponent as ProIcon } from 'assets/images/icons/icn-pro.svg';
import { ReactComponent as ProOrangeOutlineIcon } from 'assets/images/icons/icn_Pro_orange_outline.svg';

const DROPDOWN_OPEN_EVENT = 'selection-dropdown-profile-open';

export default function SelectionDropdownProfile({
  menuList = [],
  value,
  onChangeItem,
  onCreateNewProfile,
  disabled = false,
  width = '286px',
  createLabel = 'Create New Profile',
  placeholder = 'Select',
}) {
  const theme = useTheme();
  const serviceStatus = useZustandStore(state => state.serviceStatus);
  const isUnityPro = serviceStatus?.services.unityPro;

  const [anchorEl, setAnchorEl] = useState(null);
  const [chevronHover, setChevronHover] = useState(false);
  const [menuWidthPx, setMenuWidthPx] = useState(width);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const instanceIdRef = useRef(`selection-dropdown-profile-${Math.random().toString(36).slice(2)}`);
  const menuOpen = Boolean(anchorEl);

  const selectedOption = menuList.find(opt => opt.value === value);
  const selectedLabel = selectedOption?.label ?? '';

  const handleOpen = event => {
    if (disabled) return;
    event.stopPropagation();

    if (menuOpen) {
      handleClose();
      return;
    }

    triggerRef.current = event.currentTarget;
    window.dispatchEvent(
      new CustomEvent(DROPDOWN_OPEN_EVENT, {
        detail: {
          id: instanceIdRef.current,
        },
      }),
    );
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setChevronHover(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  const handleSelectItem = opt => event => {
    event.stopPropagation();
    onChangeItem?.(opt);
    handleClose();
  };

  const handleCreateNewProfile = event => {
    event.stopPropagation();
    onCreateNewProfile?.();
    handleClose();
  };

  useEffect(() => {
    if (containerRef.current) setMenuWidthPx(containerRef.current.offsetWidth);
  }, [width]);

  useEffect(() => {
    const closeWhenAnotherOpen = event => {
      if (event?.detail?.id !== instanceIdRef.current) {
        setAnchorEl(null);
        setChevronHover(false);
      }
    };

    window.addEventListener(DROPDOWN_OPEN_EVENT, closeWhenAnotherOpen);

    return () => {
      window.removeEventListener(DROPDOWN_OPEN_EVENT, closeWhenAnotherOpen);
    };
  }, []);

  const renderChevron = () => {
    if (menuOpen) {
      return chevronHover ? <ChevronUpHover /> : <ChevronUp />;
    }
    return chevronHover ? <ChevronDownHover /> : <DropdownIcon />;
  };

  const proIconRender = (type, isUnityPro) => {
    if (type && type !== 'CLICK_THROUGH' && type !== 'USERNAME_PASSWORD') {
      return isUnityPro ? (
        <ProIcon />
      ) : (
        <Tooltip
          title="Pro feature disabled."
          arrow
          placement="bottom"
          componentsProps={{
            tooltip: { sx: { bgcolor: '#4D4D4D', color: '#ffffff', px: 1.5, py: 0.5, borderRadius: '6px' } },
            arrow: { sx: { color: '#4D4D4D' } },
          }}
        >
          <ProOrangeOutlineIcon />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <Box sx={{ overflow: 'visible' }}>
      <Box
        ref={containerRef}
        onClick={handleOpen}
        onMouseEnter={() => !disabled && setChevronHover(true)}
        onMouseLeave={() => !disabled && setChevronHover(false)}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${
            disabled
              ? theme.palette.primary.borderGreyPrimary
              : menuOpen
                ? theme.palette.primary.main
                : chevronHover
                  ? theme.palette.primary.mainHover
                  : theme.palette.primary.greyFifth
          }`,
          borderRadius: theme.shape.customBorderRadius[1],
          width,
          height: 30,
          bgcolor: theme.palette.primary.white,
          px: 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
        tabIndex={disabled ? -1 : 0}
      >
        <Typography
          sx={{
            flexGrow: 1,
            fontSize: theme.typography.customFontSize[2],
            color: selectedLabel ? theme.palette.primary.greyPrimary : theme.palette.primary.greyThird,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            lineHeight: '44px',
          }}
        >
          {selectedLabel || placeholder}
          {proIconRender(selectedOption?.type, isUnityPro)}
        </Typography>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 32,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {renderChevron()}
        </Box>
      </Box>

      {menuOpen && (
        <Popper
          open={menuOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          disablePortal={false}
          sx={{ zIndex: theme.zIndex.modal, width: menuWidthPx }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={0}
              sx={{
                width: menuWidthPx,
                borderRadius: theme.shape.customBorderRadius[1],
                border: `1px solid ${theme.palette.primary.greyFifth}`,
                boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
              }}
            >
              <List
                className={mainStyle['custom-scrollbar-menu']}
                sx={{
                  pt: '5px',
                  pl: '10px',
                  boxSizing: 'content-box',
                  mr: '10px',
                  maxHeight: '180px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                {menuList.map(opt => (
                  <ListItem key={opt.value} disablePadding>
                    <ListItemButton
                      selected={value === opt.value}
                      onClick={handleSelectItem(opt)}
                      sx={{
                        color: theme.palette.primary.main,
                        pl: '10px',
                        m: '5px 5px 0 0',
                        borderRadius: theme.shape.customBorderRadius[0],
                        height: '40px',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.lightBlue,
                        },
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.subBlue,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.subBlue,
                          },
                        },
                      }}
                    >
                      <ListItemText
                        primary={opt.label}
                        primaryTypographyProps={{
                          sx: {
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            fontSize: theme.typography.customFontSize[2],
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                      {proIconRender(opt?.type, isUnityPro)}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {onCreateNewProfile && (
                <>
                  <Divider sx={{ mx: 1.5, borderColor: theme.palette.primary.greyThread }} />
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={handleCreateNewProfile}
                      sx={{
                        color: theme.palette.primary.main,
                        pl: '20px',
                        py: 1.25,
                        gap: 1,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.lightBlue,
                        },
                      }}
                    >
                      <AddIcon />
                      <Typography
                        sx={{
                          fontSize: theme.typography.customFontSize[2],
                          color: theme.palette.primary.main,
                        }}
                      >
                        {createLabel}
                      </Typography>
                    </ListItemButton>
                  </ListItem>
                </>
              )}
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
}
